import gql from "graphql-tag"

const SEARCH_ORDER_OPERATIONS = gql`
  query searchOrderOperations(
    $from: Int
    $size: Int
    $query: AWSJSON
    $aggs: AWSJSON
    $sort: [SearchSorting!]
    $trackTotalHits: Boolean
  ) {
    searchOrderOperations(
      from: $from
      size: $size
      query: $query
      aggs: $aggs
      sort: $sort
      trackTotalHits: $trackTotalHits
    ) {
      total
      aggregations
      hits {
        # Common fields (available on all operation types via OrderOperationSearchHit interface)
        completed
        countryCode
        created
        createdBy {
          id
          name
          type
        }
        failed
        id
        orderId
        orderReference
        reason {
          cause
          code
        }
        restarted
        restartedBy {
          id
          name
          type
        }
        restarts
        revision
        started
        startedBy {
          id
          name
          type
        }
        storeGroupId
        type
        updated

        # Refund-specific fields
        ... on OrderRefundSearchHit {
          orderLines {
            currencyCode
            orderLineId
            quantity
            totalAmount
            totalDiscountAmount
            totalTaxAmount
          }
          giftCards {
            amount
            currencyCode
            giftCardId
            status
          }
          giftCardProducts {
            currencyCode
            giftCardProductId
            priceAmount
            status
          }
          gifts {
            giftId
            quantity
          }
          refundedPayment {
            reference
          }
          totals {
            bonusTotal
            fee
            giftCardTotal
            grandTotal
            shippingTotal
            subTotal
            taxTotal
          }
          shippingFees {
            currencyCode
            shippingFeeId
            totalAmount
            totalDiscountAmount
            totalTaxAmount
          }
          fee {
            amount
            currencyCode
            name
          }
          paymentProvider {
            providerId
            providerName
          }
          bonusProvider {
            providerId
            providerName
          }
          giftCardProvider {
            providerId
            providerName
          }
          giftCardProductProvider {
            providerId
            providerName
          }
          shippingProvider {
            providerId
            providerName
          }
          promotionProvider {
            providerId
            providerName
          }
          voucherProvider {
            providerId
            providerName
          }
        }

        # Delivery-specific fields
        ... on OrderDeliverySearchHit {
          orderLines {
            currencyCode
            orderLineId
            quantity
            totalAmount
            totalDiscountAmount
            totalTaxAmount
          }
          shippingFees {
            currencyCode
            shippingFeeId
            totalAmount
            totalDiscountAmount
            totalTaxAmount
          }
          tracking {
            URL
            reference
            shippingCompany
            shippingMethod
          }
          bonusProvider {
            providerId
            providerName
          }
          giftCardProvider {
            providerId
            providerName
          }
          giftCardProductProvider {
            providerId
            providerName
          }
          paymentProvider {
            providerId
            providerName
          }
          shippingProvider {
            providerId
            providerName
          }
          promotionProvider {
            providerId
            providerName
          }
          voucherProvider {
            providerId
            providerName
          }
        }

        # Cancellation-specific fields
        ... on OrderCancellationSearchHit {
          cancellationBonus: bonus {
            status
            reservationId
          }
          giftCards {
            giftCardId
            reservationId
            status
            transactionId
          }
          giftCardProducts {
            cancelTransactionId
            giftCardProductId
            status
          }
          cancelledPayment {
            Reference
          }
          bonusProvider {
            providerId
            providerName
          }
          giftCardProvider {
            providerId
            providerName
          }
          giftCardProductsProvider {
            providerId
            providerName
          }
          paymentProvider {
            providerId
            providerName
          }
          shippingProvider {
            providerId
            providerName
          }
          promotionProvider {
            providerId
            providerName
          }
          voucherProvider {
            providerId
            providerName
          }
        }

        # Release-specific fields
        ... on OrderReleaseSearchHit {
          orderLines {
            currencyCode
            orderLineId
            quantity
            totalAmount
            totalDiscountAmount
            totalTaxAmount
          }
          giftCards {
            amount
            currencyCode
            giftCardId
            status
          }
          giftCardProducts {
            currencyCode
            giftCardProductId
            priceAmount
            status
          }
          gifts {
            giftId
            quantity
          }
          refundedPayment {
            amount
            reference
          }
          totals {
            bonusTotal
            giftCardTotal
            grandTotal
            paymentTotal
          }
          shippingFees {
            currencyCode
            shippingFeeId
            totalAmount
            totalDiscountAmount
            totalTaxAmount
          }
          bonus {
            amount
            currencyCode
            reservationId
            transactionId
          }
          bonusProvider {
            providerId
            providerName
          }
          giftCardProvider {
            providerId
            providerName
          }
          giftCardProductProvider {
            providerId
            providerName
          }
          paymentProvider {
            providerId
            providerName
          }
          shippingProvider {
            providerId
            providerName
          }
          promotionProvider {
            providerId
            providerName
          }
        }

        # Compensation-specific fields
        ... on OrderCompensationSearchHit {
          orderLines {
            currencyCode
            orderLineId
            quantity
            totalAmount
            totalDiscountAmount
            totalTaxAmount
          }
          giftCards {
            amount
            currencyCode
            giftCardId
            status
          }
          payment {
            amount
            currencyCode
            reference
          }
          totals {
            bonusTotal
            giftCardTotal
            grandTotal
            paymentTotal
          }
          shippingFees {
            currencyCode
            shippingFeeId
            totalAmount
            totalDiscountAmount
            totalTaxAmount
          }
          fee {
            amount
            currencyCode
            name
          }
          bonus {
            amount
            currencyCode
            reservationId
            transactionId
          }
          bonusProvider {
            providerId
            providerName
          }
          giftCardProvider {
            providerId
            providerName
          }
          compensationPaymentProvider: paymentProvider {
            providerId
            providerName
          }
          promotionProvider {
            providerId
            providerName
          }
        }

        # OrderLinesModification-specific fields
        ... on OrderOrderLinesModificationSearchHit {
          orderLines {
            modificationType
            data {
              ... on OrderLineCreate {
                id
                name
                imageUrl
                quantity
                basePriceAmount
                salePriceAmount
                totalPriceAmount
                discountAmount
              }
              ... on OrderLineUpdate {
                id
                name
                displayName
                imageUrl
                price {
                  quantity
                  basePriceAmount
                  salePriceAmount
                  totalPriceAmount
                  discountAmount
                }
              }
              ... on OrderLineDelete {
                id
                name
                imageUrl
                quantity
                basePriceAmount
                salePriceAmount
                totalPriceAmount
                discountAmount
              }
            }
            prev {
              ... on OrderLineCreate {
                id
                name
                imageUrl
                quantity
                basePriceAmount
                salePriceAmount
                totalPriceAmount
                discountAmount
              }
              ... on OrderLineUpdate {
                id
                name
                displayName
                imageUrl
                price {
                  quantity
                  basePriceAmount
                  salePriceAmount
                  totalPriceAmount
                  discountAmount
                }
              }
              ... on OrderLineDelete {
                id
                name
                imageUrl
                quantity
                basePriceAmount
                salePriceAmount
                totalPriceAmount
                discountAmount
              }
            }
          }
          orderLinesModificationPaymentProvider: paymentProvider {
            providerId
            providerName
          }
          bonusProvider {
            providerId
            providerName
          }
          giftCardProvider {
            providerId
            providerName
          }
          giftCardProductProvider {
            providerId
            providerName
          }
          shippingProvider {
            providerId
            providerName
          }
          promotionProvider {
            providerId
            providerName
          }
          voucherProvider {
            providerId
            providerName
          }
        }
      }
    }
  }
`

export default SEARCH_ORDER_OPERATIONS
